import React from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts'

const number = value => Number(value) || 0
const displayNumber = value => number(value).toLocaleString('th-TH-u-nu-latn')

export default function AnalyticsOverview({ stats = {}, divisionStats = [], loading = false, error = '' }) {
  const pending = number(stats.pending)
  const witnesses = number(stats.signed) + number(stats.witness1_signed)
  const mayor = number(stats.approved)
  const completed = number(stats.completed)
  const other = Math.max(0, number(stats.total) - pending - witnesses - mayor - completed)
  const cards = [
    { label: 'สัญญาทั้งหมด', value: stats.total, icon: 'ti-files', tone: 'all' },
    { label: 'รอพนักงานลงนาม', value: pending, icon: 'ti-clock-hour-4', tone: 'pending' },
    { label: 'รอพยานลงนาม', value: witnesses, icon: 'ti-signature', tone: 'witness' },
    { label: 'รอนายกลงนาม', value: mayor, icon: 'ti-writing', tone: 'mayor' },
    { label: 'ลงนามครบแล้ว', value: completed, icon: 'ti-circle-check', tone: 'complete' },
  ]
  const portions = [
    { name: 'รอพนักงานลงนาม', value: pending, color: '#83c7ed' },
    { name: 'รอพยานลงนาม', value: witnesses, color: '#57b4e8' },
    { name: 'รอนายกลงนาม', value: mayor, color: '#2e91db' },
    { name: 'ลงนามครบแล้ว', value: completed, color: '#1679d4' },
    { name: 'สถานะอื่น', value: other, color: '#dbeaf6' },
  ].filter(item => item.value > 0)
  const rows = Array.isArray(divisionStats) ? divisionStats : []
  const chartRows = rows.map(row => ({ ...row, total: number(row.total) }))

  return <div className="a-analytics">
    <section className="a-stat-grid" aria-label="สถิติสัญญา">
      {cards.map(card => <article className={`a-stat a-stat-${card.tone}`} key={card.label}>
        <span className="a-stat-icon" aria-hidden="true"><i className={`ti ${card.icon}`} /></span>
        <strong>{loading || error ? '—' : displayNumber(card.value)}</strong>
        <span>{card.label}</span>
      </article>)}
    </section>
    <section className="a-chart-grid" aria-label="กราฟสรุปสัญญา">
      <div className="a-panel">
        <h2>สัญญาแยกตามกองงาน</h2>
        {loading ? <p className="a-chart-empty">กำลังโหลดข้อมูล…</p>
          : error ? <p className="a-chart-empty" role="alert">{error}</p>
          : chartRows.length ? <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartRows} margin={{ top: 16, right: 12, bottom: 12, left: 0 }}>
              <defs><linearGradient id="a-report-bars" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7cc5ec" /><stop offset="100%" stopColor="#328fdc" />
              </linearGradient></defs>
              <CartesianGrid stroke="#d9e9f8" vertical={false} />
              <XAxis dataKey="division_name" tick={{ fontSize: 12, fill: '#567791' }} tickFormatter={name => name?.length > 18 ? `${name.slice(0, 18)}…` : name} interval={0} />
              <YAxis allowDecimals={false} width={36} tick={{ fontSize: 12, fill: '#567791' }} />
              <Tooltip formatter={value => [`${displayNumber(value)} สัญญา`, 'จำนวน']} contentStyle={{ border: '1px solid #d9e9f8', borderRadius: 12, fontFamily: 'Sarabun' }} />
              <Bar dataKey="total" name="จำนวนสัญญา" fill="url(#a-report-bars)" radius={[8, 8, 0, 0]} maxBarSize={78} />
            </BarChart>
          </ResponsiveContainer> : <p className="a-chart-empty">ยังไม่มีข้อมูลสัญญาแยกตามกองงาน</p>}
      </div>
      <div className="a-panel">
        <h2>สัดส่วนสถานะสัญญา</h2>
        {loading ? <p className="a-chart-empty">กำลังโหลดข้อมูล…</p>
          : error ? <p className="a-chart-empty" role="alert">{error}</p>
          : portions.length ? <>
            <div className="a-donut-wrap">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart><Pie data={portions} dataKey="value" innerRadius={73} outerRadius={106} startAngle={90} endAngle={-270} stroke="none">
                  {portions.map(item => <Cell key={item.name} fill={item.color} />)}
                </Pie><Tooltip formatter={value => `${displayNumber(value)} สัญญา`} contentStyle={{ border: '1px solid #d9e9f8', borderRadius: 12, fontFamily: 'Sarabun' }} /></PieChart>
              </ResponsiveContainer>
              <div className="a-donut-center"><strong>{displayNumber(completed)}</strong><span>ลงนามครบแล้ว</span></div>
            </div>
            <div className="a-chart-legend">{portions.map(item => <span key={item.name}><i style={{ background: item.color }} />{item.name} {displayNumber(item.value)}</span>)}</div>
          </> : <p className="a-chart-empty">ยังไม่มีข้อมูลสถานะสัญญา</p>}
      </div>
    </section>
    <section className="a-panel a-division-panel">
      <h2>รายละเอียดแยกตามกองงาน</h2>
      <div className="a-table-scroll"><table>
        <thead><tr><th>กองงาน</th><th>ทั้งหมด</th><th>รอพนักงาน</th><th>รอพยาน</th><th>รอนายก</th><th>ลงนามครบแล้ว</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.division_name}>
          <td>{row.division_name}</td><td>{displayNumber(row.total)}</td><td>{displayNumber(row.pending)}</td>
          <td>{displayNumber(number(row.signed) + number(row.witness1_signed))}</td><td>{displayNumber(row.approved)}</td><td>{displayNumber(row.completed)}</td>
        </tr>)}</tbody>
      </table>{!loading && !rows.length && <p className="a-table-empty">{error || 'ยังไม่มีข้อมูลแยกตามกองงาน'}</p>}</div>
    </section>
  </div>
}
